"use client";

import { useEffect, useState } from 'react';
import { useMediaQuery } from '@mantine/hooks';
import TmuxLiveTerminal from './TmuxLiveTerminal';

type BridgeSession = {
  chatId: string;
  tmuxSession: string;
  lastActivityAt: string;
  idleCompactedAt?: string | null;
  codexSessionId?: string | null;
  lastOutputAt?: string | null;
  lastError?: string | null;
  tmuxAlive: boolean;
  paneCurrentCommand: string | null;
  idleMs: number;
  transcriptText?: string | null;
  pendingCount?: number;
  pendingFlushAt?: string | null;
  pendingPreview?: string | null;
  lastStructuredDigestText?: string | null;
  lastStructuredDigestKind?: string | null;
  lastStructuredDigestAt?: string | null;
};

type BridgeStatus = {
  ok: boolean;
  sessions: BridgeSession[];
  updateOffset: number;
  repoRoot: string;
  idleCompactMs: number;
  watchIntervalMs: number;
};

function fmtDate(value: string | null | undefined): string {
  if (!value) return 'Chưa có';
  return new Date(value).toLocaleString();
}

function fmtDuration(ms: number): string {
  if (!Number.isFinite(ms)) return 'n/a';
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

export default function TelegramCodexPage() {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [status, setStatus] = useState<BridgeStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [compactingChatId, setCompactingChatId] = useState<string | null>(null);

  async function load(mode: 'initial' | 'refresh' = 'refresh') {
    if (mode === 'initial') {
      setLoading(true);
    } else {
      setRefreshing(true);
    }
    try {
      const response = await fetch('/api/telegram-codex/status', { cache: 'no-store' });
      if (!response.ok) {
        if (mode === 'initial') {
          setStatus(null);
        }
        return;
      }
      setStatus((await response.json()) as BridgeStatus);
    } finally {
      if (mode === 'initial') {
        setLoading(false);
      } else {
        setRefreshing(false);
      }
    }
  }

  useEffect(() => {
    load('initial').catch(console.error);
    const timer = window.setInterval(() => load('refresh').catch(console.error), 10_000);
    return () => window.clearInterval(timer);
  }, []);

  async function compactSession(chatId: string) {
    setCompactingChatId(chatId);
    try {
      const response = await fetch('/api/telegram-codex/compact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ chatId }),
      });
      if (!response.ok) {
        throw new Error(await response.text());
      }
      await load('refresh');
    } finally {
      setCompactingChatId(null);
    }
  }

  return (
    <div
      style={{
        padding: isMobile ? 12 : 22,
        background: 'linear-gradient(180deg, #eef5ff 0%, #ddeafe 100%)',
        minHeight: '100%',
      }}
    >
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ margin: 0, fontSize: isMobile ? 22 : 30 }}>Telegram Codex Bridge</h1>
        <div style={{ color: '#475569', marginTop: 8 }}>
          Theo dõi session Telegram → Codex sống lâu, tmux, resume, transcript, và compact thủ công.
        </div>
        {refreshing && (
          <div style={{ color: '#64748b', marginTop: 8, fontSize: 13 }}>
            Đang cập nhật dữ liệu nền…
          </div>
        )}
      </div>

      {loading ? (
        <div>Đang tải trạng thái bridge…</div>
      ) : !status ? (
        <div style={{ color: '#64748b' }}>Không đọc được trạng thái bridge.</div>
      ) : (
        <div style={{ display: 'grid', gap: 18 }}>
          <section
            style={{
              background: 'rgba(255,255,255,0.82)',
              border: '1px solid #bfd5f8',
              borderRadius: 22,
              padding: 18,
            }}
          >
            <div style={{ fontWeight: 800, fontSize: 18, marginBottom: 12 }}>Runtime</div>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, minmax(0, 1fr))', gap: 12 }}>
              <div><strong>Sessions</strong><div>{status.sessions.length}</div></div>
              <div><strong>Update offset</strong><div>{status.updateOffset}</div></div>
              <div><strong>Auto compact</strong><div>Đã tắt</div></div>
              <div><strong>Watch tick</strong><div>{fmtDuration(status.watchIntervalMs)}</div></div>
            </div>
            <div style={{ marginTop: 12, color: '#475569' }}>
              <strong>Repo:</strong> {status.repoRoot}
            </div>
          </section>

          <section style={{ display: 'grid', gap: 14 }}>
            {status.sessions.length === 0 ? (
              <div style={{ color: '#64748b' }}>Chưa có session Telegram nào được tạo.</div>
            ) : (
              status.sessions.map((session) => (
                <article
                  key={session.chatId}
                  style={{
                    background: 'rgba(255,255,255,0.88)',
                    border: '1px solid #bfd5f8',
                    borderRadius: 22,
                    padding: 18,
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                    <div>
                      <div style={{ fontSize: 18, fontWeight: 800 }}>{session.chatId}</div>
                      <div style={{ color: '#475569', marginTop: 4 }}>tmux: {session.tmuxSession}</div>
                    </div>
                    <div
                      style={{
                        alignSelf: 'flex-start',
                        display: 'flex',
                        gap: 8,
                        flexWrap: 'wrap',
                      }}
                    >
                      <div
                        style={{
                          borderRadius: 999,
                          padding: '6px 10px',
                          background: session.tmuxAlive ? '#d9f99d' : '#fecaca',
                          color: '#1f2937',
                          fontWeight: 700,
                          fontSize: 12,
                        }}
                      >
                        {session.tmuxAlive ? 'TMUX ALIVE' : 'TMUX DOWN'}
                      </div>
                      <button
                        type="button"
                        onClick={() => compactSession(session.chatId).catch(console.error)}
                        disabled={compactingChatId === session.chatId}
                        style={{
                          border: '1px solid #cbd5e1',
                          background: compactingChatId === session.chatId ? '#e2e8f0' : '#ffffff',
                          color: '#0f172a',
                          borderRadius: 999,
                          padding: '6px 12px',
                          fontWeight: 700,
                          fontSize: 12,
                          cursor: compactingChatId === session.chatId ? 'wait' : 'pointer',
                        }}
                      >
                        {compactingChatId === session.chatId ? 'Đang compact…' : 'Gửi /compact'}
                      </button>
                    </div>
                  </div>

                  <div
                    style={{
                      marginTop: 14,
                      display: 'grid',
                      gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, minmax(0, 1fr))',
                      gap: 12,
                    }}
                  >
                    <div><strong>Current command</strong><div>{session.paneCurrentCommand || 'n/a'}</div></div>
                    <div><strong>Last activity</strong><div>{fmtDate(session.lastActivityAt)}</div></div>
                    <div><strong>Idle</strong><div>{fmtDuration(session.idleMs)}</div></div>
                    <div><strong>Codex session</strong><div>{session.codexSessionId || 'Chưa parse được'}</div></div>
                    <div><strong>Last output</strong><div>{fmtDate(session.lastOutputAt)}</div></div>
                    <div><strong>Idle compacted</strong><div>{fmtDate(session.idleCompactedAt)}</div></div>
                    <div><strong>Queued messages</strong><div>{session.pendingCount || 0}</div></div>
                    <div><strong>Next flush</strong><div>{fmtDate(session.pendingFlushAt)}</div></div>
                  </div>

                  {session.lastError && (
                    <div
                      style={{
                        marginTop: 14,
                        background: '#fff1f2',
                        border: '1px solid #fecdd3',
                        color: '#9f1239',
                        borderRadius: 14,
                        padding: 12,
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      <strong>Last error</strong>
                      <div>{session.lastError}</div>
                    </div>
                  )}

                  {session.lastStructuredDigestText && (
                    <div
                      style={{
                        marginTop: 14,
                        background: '#eff6ff',
                        border: '1px solid #bfdbfe',
                        color: '#0f172a',
                        borderRadius: 14,
                        padding: 12,
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      <strong>Latest digest</strong>
                      <div style={{ color: '#475569', fontSize: 12, marginTop: 4, marginBottom: 8 }}>
                        {session.lastStructuredDigestKind || 'unknown'} · {fmtDate(session.lastStructuredDigestAt)}
                      </div>
                      <div>{session.lastStructuredDigestText}</div>
                    </div>
                  )}

                  <div
                    style={{
                      marginTop: 14,
                      background: '#111827',
                      color: '#f8fafc',
                      borderRadius: 14,
                      padding: 12,
                    }}
                  >
                    <strong style={{ display: 'block', marginBottom: 10 }}>Transcript history</strong>
                    <pre
                      style={{
                        margin: 0,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        fontSize: 12,
                        lineHeight: 1.5,
                        maxHeight: 420,
                        overflow: 'auto',
                      }}
                    >
                      {session.transcriptText?.trim() || 'Chua co transcript hoi thoai.'}
                    </pre>
                  </div>

                  <TmuxLiveTerminal chatId={session.chatId} />

                  <div
                    style={{
                      marginTop: 14,
                      background: '#1e293b',
                      color: '#e2e8f0',
                      borderRadius: 14,
                      padding: 12,
                    }}
                  >
                    <strong style={{ display: 'block', marginBottom: 10 }}>Queued input context</strong>
                    <pre
                      style={{
                        margin: 0,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        fontSize: 12,
                        lineHeight: 1.5,
                        maxHeight: 220,
                        overflow: 'auto',
                      }}
                    >
                      {session.pendingPreview?.trim() || 'Khong co tin nhan dang cho submit.'}
                    </pre>
                  </div>
                </article>
              ))
            )}
          </section>
        </div>
      )}
    </div>
  );
}
